/**
 * InspectionDetailPage - Full detail view for a single inspection.
 *
 * Displays all inspection information organized into tabbed sections:
 * - Details: customer, building, contact, inspector, assignee, dates, general notes
 * - Observations: photo grid (2 columns) showing observation photos + captions.
 *	 Includes add photo button for inspectors.
 * - Completed QC: photo grid showing correction photos + captions.
 *	 Includes add photo button for the assigned technician.
 *
 * Action buttons based on inspection status:
 * - pending: "Start Inspection"
 * - in_progress: "Complete Observations" (transitions to observations_complete)
 * - observations_complete: "Complete Inspection" (transitions to completed)
 *	 leads on a clean result or once a follow-up exists; "Create Follow-Up"
 *	 (spins out a work order) leads when an item is rated Critical / Needs
 *	 Attention and nothing has been spun out yet
 * - corrections_in_progress: "Complete Corrections" (transitions to completed).
 *	 Legacy — nothing enters this status any more, but rows already in it
 *	 still need the way out
 * - completed: "Generate Pre-Correction PDF", "Generate Post-Correction PDF"
 *
 * IMPORTANT: Inspections are SEPARATE from work orders — never mixed.
 *
 * URL deep-links honored:
 * - /inspections/:id/edit  → auto-opens the edit modal (mirrors WO).
 * - ?workflow=1            → auto-opens the 3-step InspectionWorkflow
 *                            overlay (Checklist → Verify → Sign-Off).
 *                            Used by the inspections list-page Start /
 *                            Resume CTAs.
 * - ?tab=<name>            → lands on a specific tab. Reserved for
 *                            generic deep-links; Start / Resume use
 *                            ?workflow=1 instead.
 *
 * @route /inspections/:id
 * @route /inspections/:id/edit
 */
import React, { useState, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useDeleteJob } from '../../hooks/useDeleteJob';
import { inspectionService } from '../../services/inspectionService';
import { qk } from '../../lib/queryKeys';
import { invalidateJobCaches } from '../../lib/invalidateJobCaches';
import type { Inspection, InspectionPhoto } from '../../services/inspectionService';
import { normalizeInspection } from '../../services/inspectionService';
import { checklistInstanceService } from '../../services/checklistInstanceService';
import type { ChecklistInstance, ChecklistItemRating } from '../../services/checklistInstanceService';
import { checklistService } from '../../services/checklistService';
import type { ChecklistItem } from '../../services/checklistService';
import { InspectionChecklist, InspectionWorkflow, PhotoLightbox } from '../../components/organisms';
import { EventTypeBadge, BackLink, Icon } from '../../components/atoms';
import { DetailActionBar, InfoRow, InfoSection } from '../../components/molecules';
import type { DetailAction } from '../../components/molecules';
import { photoTypeService } from '../../services/photoTypeService';
import { camelizeKeys } from '../../services/api';
import { PhotoGrid } from '../../components/organisms/PhotoGrid';
import { getStatusTransitions } from '../../utils/statusTransitions';
import { getJobAction } from '../../utils/jobAction';
import Modal from '../../components/organisms/Modal/Modal';
import { JobForm } from '../../components/organisms/JobForm/JobForm';
import { Spinner } from '../../components/atoms/Spinner';
import { uploadService } from '../../services/uploadService';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import { useNotifyPrompt } from '../../hooks/useNotifyPrompt';
import { useDiscardGuard } from '../../hooks/useDiscardGuard';
import type { CapturedPhoto } from '../../components/organisms/PhotoCapture';
import { capturedPhotoToFile } from '../../components/organisms/PhotoCapture';
import { usePhotoUploadQueue } from '../../hooks/usePhotoUploadQueue';
import { STORE_NAMES } from '../../services/offlineStorage';
import { formatDate, formatDateTime, formatDay } from '../../utils/formatDate';
import { formatLabel } from '../../utils/formatLabel';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { generateInspectionPdfWithPhotos } from '../../utils/pdfGenerator';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Tab identifiers for the detail page sections */
type DetailTab = 'details' | 'checklist' | 'observations' | 'completed_qc';

/** Status badge color classes */
const STATUS_COLORS: Record<string, string> = {
	pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
	in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
	observations_complete: 'bg-indigo-100 text-indigo-800 border-indigo-300',
	corrections_in_progress: 'bg-orange-100 text-orange-800 border-orange-300',
	completed: 'bg-green-100 text-green-800 border-green-300',
	cancelled: 'bg-red-100 text-red-800 border-red-300',
};

/**
 * Pill classes for a follow-up work order's status. Three buckets are enough
 * here: the reviewer wants to know whether the work is done, still open, or
 * was called off — not which work-order stage it is in.
 */
function followUpStatusClass(status: string): string {
	if (status === 'cancelled') return 'bg-gray-100 text-gray-600 border-gray-300';
	if (getJobAction(status).kind === 'view') return 'bg-green-100 text-green-800 border-green-300';
	return 'bg-blue-100 text-blue-800 border-blue-300';
}

const InspectionDetailPage: React.FC = () => {
	/* Extract the inspection ID from the URL params */
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { user, hasAnyRole } = useAuth();

	/* "Notify users" gate for the status buttons. Completing an inspection
	 * emails the inspector and the completion watch list — and, when "Send to
	 * Client" is on, the customer contact. An admin sees that list, and can
	 * trim it, before the transition commits. */
	const { requestNotifyDecision, notifyPrompt } = useNotifyPrompt({ lead: 'This status change will email' });

	/* Auto-open edit modal when navigating to /inspections/:id/edit */
	const isEditRoute = location.pathname.endsWith('/edit');

	/* Auto-select a tab when ?tab=<name> is on the URL. Reserved for
	 * generic deep-links that want to land on a specific tab — list-page
	 * Start / Resume CTAs no longer use this; they use ?workflow=1
	 * (handled below) to open the InspectionWorkflow overlay. */
	const requestedTab: DetailTab | null = (() => {
		const t = new URLSearchParams(location.search).get('tab');
		const valid: DetailTab[] = ['details', 'checklist', 'observations', 'completed_qc'];
		return valid.includes(t as DetailTab) ? (t as DetailTab) : null;
	})();

	/* Auto-open the workflow overlay when ?workflow=1 is on the URL.
	 * Mirrors the work-order detail page so the same deep-link convention
	 * works for both surfaces. */
	const wantsWorkflow = new URLSearchParams(location.search).get('workflow') === '1';

	/* Network connectivity state — used to decide between online upload vs offline queue */
	const { isOnline, addPendingItem } = useOfflineSync();

	/* Background photo uploads — same queue the work-order page uses. Capturing
	   hands off immediately so an inspector can keep walking the building while
	   photos upload behind them. `meta.photoType` routes each in-flight tile to
	   the Observation or Correction grid. */
	const photoUploads = usePhotoUploadQueue<{ photoType: 'observation' | 'correction' }>();

	const queryClient = useQueryClient();

	/* --- UI state --- */
	const [activeTab, setActiveTab] = useState<DetailTab>(requestedTab ?? 'details');

	/* --- PDF generation state --- */
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	/** Controls visibility of the edit inspection modal */
	const [showEditModal, setShowEditModal] = useState(isEditRoute);
	/* The edit form's unsaved edits, so closing the modal by accident —
	   Escape, a click outside, the X — asks before throwing them away. */
	const [editFormDirty, setEditFormDirty] = useState(false);
	const closeEditModal = () => {
		setShowEditModal(false);
		setEditFormDirty(false);
	};
	const { requestClose: requestCloseEditModal, discardPrompt: discardEditPrompt } = useDiscardGuard({ isDirty: editFormDirty, onClose: closeEditModal });
	/** Controls visibility of the 3-step inspection workflow overlay.
	 *  Initialized true when the URL carries ?workflow=1 so deep-links
	 *  (e.g. from a future Start CTA) auto-open the surface. */
	const [showWorkflow, setShowWorkflow] = useState(wantsWorkflow);

	/* --- Checklist tab: bottom textareas + lightbox. --- */
	/** Lightbox state for tapping a photo thumbnail on a row. */
	const [lightbox, setLightbox] = useState<{ url: string; caption: string | null; area: string | null } | null>(null);

	/* Role-based visibility flags — super_admin inherits admin privileges */
	const canEdit = hasAnyRole(['super_admin', 'admin', 'manager']);
	/* Delete lives behind the kebab (managers and above, matching the
	   server's gate); after it goes there is nothing to show, so return
	   to the list. */
	const { requestDelete, deleteModal } = useDeleteJob('inspection', { onDeleted: () => navigate('/inspections') });

	/**
	 * Inspection read (TanStack Query). isLoading is true only on the first
	 * load; background refetches after mutations update silently.
	 */
	const inspectionQuery = useQuery({
		queryKey: qk.inspections.detail(id ?? ''),
		enabled: !!id,
		queryFn: async (): Promise<Inspection> => {
			const response = await inspectionService.getById(id as string);
			/* Backend wraps single resources in { data: ... } and uses snake_case keys.
			 * Unwrap and camelize to match the Inspection interface. */
			const raw = (response.data as any)?.data ?? response.data;
			const camelized = camelizeKeys(raw) as Record<string, unknown>;
			return normalizeInspection(camelized);
		},
	});

	const inspection: Inspection | null = inspectionQuery.data ?? null;
	const isLoading = !!id && inspectionQuery.isLoading;
	const error = inspectionQuery.error ? (inspectionQuery.error as Error).message : null;

	/**
	 * Refetch the inspection. Named `fetchInspection` for the many call sites
	 * that refresh after a mutation (status change, field save, photo add/delete,
	 * signature). Also invalidates the inspection lists plus the Dashboard reads
	 * so those surfaces stay fresh — a status change here moves Dashboard counts
	 * and the day's task feed. `listsOnly` because the record itself is
	 * refetched directly above; invalidating its detail key would double-fetch.
	 */
	const fetchInspection = useCallback(async () => {
		await inspectionQuery.refetch();
		invalidateJobCaches(queryClient, 'inspection', { listsOnly: true });
	}, [inspectionQuery, queryClient]);

	/**
	 * Polymorphic checklist instance attached to this inspection (null when none).
	 * Cached via TanStack; re-fetched after the edit modal closes.
	 */
	const checklistInstanceQuery = useQuery({
		queryKey: qk.checklistInstances.detail(`insp-${id ?? ''}`),
		enabled: !!id,
		queryFn: async (): Promise<ChecklistInstance | null> => {
			try {
				const res = await checklistInstanceService.getForInspection(id as string);
				return ((res.data as any)?.data ?? null) as ChecklistInstance | null;
			} catch {
				return null;
			}
		},
	});
	const checklistInstance: ChecklistInstance | null = checklistInstanceQuery.data ?? null;
	const fetchChecklistInstance = useCallback(async () => {
		await checklistInstanceQuery.refetch();
	}, [checklistInstanceQuery]);

	/**
	 * Checklist items + per-item photos via checklistService. Used by the
	 * Checklist tab's rating UI; cached and refreshed after any rating change.
	 */
	const checklistItemsKey = qk.checklistInstances.detail(`insp-items-${id ?? ''}`);
	const checklistItemsQuery = useQuery<ChecklistItem[]>({
		queryKey: checklistItemsKey,
		enabled: !!id,
		queryFn: async (): Promise<ChecklistItem[]> => {
			try {
				const res = await checklistService.getInspectionChecklist(id as string);
				return res.data?.items ?? [];
			} catch {
				return [];
			}
		},
	});
	const checklistItems: ChecklistItem[] = checklistItemsQuery.data ?? [];
	const fetchChecklistItems = useCallback(async () => {
		await checklistItemsQuery.refetch();
	}, [checklistItemsQuery]);

	/**
	 * Persist a rating change for one item. Optimistic cache update first
	 * so the UI feels instant, then refetch on success to pick up
	 * server-side attribution (completedByName + completedAt) and
	 * any sibling-tab edits.
	 */
	const handleRateItem = useCallback(async (itemId: string, rating: ChecklistItemRating | null, ratingNote: string | null) => {
		queryClient.setQueryData<ChecklistItem[]>(checklistItemsKey, (prev: ChecklistItem[] | undefined) =>
			(prev ?? []).map((it: ChecklistItem) => it.id === itemId ? { ...it, rating, ratingNote } : it));
		try {
			await checklistInstanceService.rateItem(itemId, rating, ratingNote);
			await fetchChecklistItems();
		} catch (err) {
			alert(apiErrorMessage(err, 'Could not save that rating. Please try again.'));
			await fetchChecklistItems();
		}
	}, [fetchChecklistItems, queryClient, checklistItemsKey]);

	/**
	 * PATCH the inspection record with the supplied field. Used by
	 * the Recommendations / Remarks and Follow-Up textareas, which
	 * autosave on blur via InspectionChecklist.
	 */
	const handleSaveInspectionField = useCallback(async (field: 'recommendationsRemarks' | 'followUpNotes', value: string) => {
		if (!id) return;
		try {
			await inspectionService.update(id, { [field]: value });
			await fetchInspection();
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to save');
		}
	}, [id, fetchInspection]);

	/**
	 * Handles a status transition by PATCHing the new status to the API.
	 *
	 * When transitioning into `in_progress`, the workflow overlay opens
	 * automatically so the inspector lands on Step 1 (rating). Mirrors
	 * the work-order detail page's auto-open behavior so the field UX
	 * is consistent.
	 */
	const handleStatusTransition = async (newStatus: string) => {
		if (!id) return;

		/* Ask who this transition would email before anything moves — first,
		   so cancelling at the prompt leaves the inspection where it was and
		   doesn't open the overlay below. Transitions that email nobody never
		   prompt. */
		const notifyDecision = await requestNotifyDecision({ kind: 'inspection', id, status: newStatus });
		if (!notifyDecision) return;

		/* Open the overlay BEFORE the await so the re-render from the
		   loading state can't reset the flag. */
		if (newStatus === 'in_progress') {
			setShowWorkflow(true);
		}
		try {
			await inspectionService.update(id, { status: newStatus as Inspection['status'], ...notifyDecision });
			await fetchInspection();
		} catch (err) {
			if (newStatus === 'in_progress') {
				setShowWorkflow(false);
			}
			alert(err instanceof Error ? err.message : 'Failed to update status');
		}
	};

	/**
	 * Renders a single PDF report for the inspection using jspdf, then
	 * triggers a download. All content comes from the already-loaded
	 * `inspection` state, so no extra network round-trip is needed.
	 */
	const handleGeneratePdf = async () => {
		if (!inspection) return;
		setIsGeneratingPdf(true);
		try {
			/* Rated items give the report its findings; the photo-embedding
			   variant puts the evidence in alongside them. It is async because
			   every image has to be fetched and downscaled first — photos that
			   fail to load are dropped, so a broken URL costs that photo and
			   not the report. */
			await generateInspectionPdfWithPhotos(inspection, checklistItems);
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to generate PDF');
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	/**
	 * Handles photo deletion by calling the API and re-fetching.
	 */
	const handleDeletePhoto = async (photoId: string) => {
		if (!id) return;
		try {
			await inspectionService.deletePhoto(id, photoId);
			await fetchInspection();
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to delete photo');
		}
	};

	/**
	 * Handles a captured photo from the PhotoCapture component.
	 *
	 * Returns immediately — the compressed photo goes onto the background
	 * upload queue and PhotoGrid shows its progress inline. See the matching
	 * handler on WorkOrderDetailPage; the two differ only in the seeded photo
	 * type names and which entity they attach to.
	 *
	 * @param photoType - 'observation' or 'correction' (mapped to the seeded type name)
	 * @param photo     - The CapturedPhoto data from the PhotoCapture component
	 */
	const handlePhotoCaptured = useCallback(async (photoType: 'observation' | 'correction', photo: CapturedPhoto) => {
		if (!id) return;

		/* Inspection seed names are "Observation" and "Completed QC" — the
		   frontend's 'correction' is the post-fix QC photo. */
		const seededName = photoType === 'observation' ? 'Observation' : 'Completed QC';

		if (!isOnline) {
			/* Offline: persist to IndexedDB so the capture survives a reload and
			   syncService.syncPhotos() can push it on reconnect. The type is
			   stored by name because resolving the id needs the network. */
			try {
				await addPendingItem(STORE_NAMES.PHOTOS, {
					parentType: 'inspection',
					parentId: id,
					photoTypeName: seededName,
					base64: photo.base64,
					fileName: photo.fileName,
					mimeType: photo.mimeType,
					companyId: inspection?.companyId ? String(inspection.companyId) : null,
					caption: photo.fileName,
					capturedAt: photo.capturedAt,
				});
				alert('Photo saved on this device. It will upload when you are back on a signal.');
			} catch (err) {
				alert('Failed to save the photo for offline sync.');
			}
			return;
		}

		/* Online: enqueue and return. The photo-type lookup runs inside the
		   background task so its round trip doesn't block the UI either. */
		photoUploads.enqueue({
			previewUrl: photo.base64,
			meta: { photoType },
			upload: async () => {
				const photoTypeId = inspection?.companyId
					? await photoTypeService.resolveByName(String(inspection.companyId), seededName)
					: null;
				if (!photoTypeId) {
					throw new Error(`No "${seededName}" photo type is configured for this company. Ask a Super Admin to seed it in the CMS.`);
				}

				const file = await capturedPhotoToFile(photo);
				return uploadService.uploadInspectionPhoto(id, file, {
					photoTypeId,
					caption: photo.fileName,
				});
			},
			onUploaded: () => fetchInspection(),
		});
	}, [id, isOnline, addPendingItem, photoUploads, fetchInspection, inspection?.companyId]);

	/* formatLabel lives in utils/formatLabel — see import. */

	/* --- Loading state --- */
	if (isLoading) {
		return (
			<div className="inspection-detail-page__loading p-24px flex items-center justify-center min-h-[400px]">
				<Spinner size="lg" label="Loading inspection" />
			</div>
		);
	}

	/* --- Error state --- */
	if (error || !inspection) {
		return (
			<div className="p-24px">
				<div className="inspection-detail-page__error bg-red-50 border border-red-200 rounded-lg p-24px text-center">
					<p className="text-14px font-sans text-red-700">{error || 'Inspection not found'}</p>
					<button type="button" onClick={() => navigate('/inspections')} className="mt-12px text-14px font-sans font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">
						Back to Inspections
					</button>
				</div>
			</div>
		);
	}

	/* Separate photos into observation and correction groups */
	const observationPhotos = (inspection.photos || []).filter((p: InspectionPhoto) => p.photoType === 'observation');
	const correctionPhotos = (inspection.photos || []).filter((p: InspectionPhoto) => p.photoType === 'correction');

	/* Follow-up work orders spun out of this inspection. Remediation for a
	   failed inspection lives on those, so this is what a reviewer reads
	   before completing it. "Open" = anything short of a terminal status. */
	const followUps = inspection.followUpWorkOrders ?? [];
	const openFollowUps = followUps.filter((wo) => getJobAction(wo.status).kind !== 'view');

	/* Any item that needs work: Critical (escalated to management) or Needs
	   Attention (follow up within the maintenance cycle). There is no dedicated
	   "failed" status on inspections, so the per-item tiers are the system's
	   signal that field-work follow-up is required. Gates Create Follow-Up. */
	const hasFailedItems = checklistItems.some(
		(item) => item.rating === 'critical' || item.rating === 'needs_attention',
	);

	/* Who is ON this inspection: the inspector, the primary assignee or an
	   additional assignee. Any of them may rate the checklist (the API lets
	   them), so any of them must be able to get back into it. This used to
	   check the inspector alone — usually whoever created the inspection —
	   so a technician it was assigned to who closed the checklist halfway was
	   locked out of their own job. Ids are compared as strings because the
	   API is not consistent about number vs string. */
	const myId = user?.id != null ? String(user.id) : null;
	const isOnInspection = myId !== null && (
		myId === String(inspection.inspectorId ?? '')
		|| myId === String(inspection.assigneeId ?? '')
		|| (inspection.assignees ?? []).some((a) => String(a.id) === myId)
	);
	/* Rating, observation photos and the workflow are all open to the same
	   people while the inspection is in progress. */
	const canWorkObservations = inspection.status === 'in_progress' && (canEdit || isOnInspection);

	/* Determine who can add observation photos (anyone on it during in_progress) */
	const canAddObservationPhotos = canWorkObservations;
	/* Determine who can add correction photos (assignee during corrections_in_progress) */
	const canAddCorrectionPhotos = inspection.status === 'corrections_in_progress' && (canEdit || user?.id === inspection.assigneeId);

	/* ============================================
	 * ACTION CLUSTER
	 * ============================================
	 * Every action on this record funnels into a single DetailActionBar. The
	 * page used to render a header button cluster AND a separate status
	 * transition bar directly beneath it, which on a phone stacked two solid
	 * orange CTAs ("Resume Workflow" and "Complete Observations") plus a full-
	 * size red "Cancel" into a ragged wall of buttons with no clear hierarchy.
	 *
	 * One bar, one hierarchy: a single solid primary, brand-outlined supporting
	 * actions, neutral utilities, and the rare/destructive ones behind the kebab. */

	/* Status steps this user may take from here. */
	const transitions = user ? getStatusTransitions('inspection', inspection.status, user.role) : [];
	/* Forward progress stays on screen; cancellation is destructive, so it moves
	   into the overflow menu rather than sitting next to the primary CTA. */
	const cancelTransition = transitions.find((t) => t.variant === 'danger');

	/* Workflow re-entry — anyone on the inspection can jump back into the
	   3-step rating + sign-off flow while it is still in progress. */
	const canResumeWorkflow = canWorkObservations;

	/* Forward progress stays on screen — except that a technician mid-checklist
	   finishes through the workflow's Submit Inspection, which insists every
	   required item is rated. "Complete Observations" beside Resume Workflow
	   moved the inspection on with nothing rated, so they don't get it here.
	   Supervisors keep it: closing out without the checklist is their call. */
	const forwardTransitions = transitions.filter((t) => t.variant !== 'danger'
		&& !(canResumeWorkflow && !canEdit && t.toStatus === 'observations_complete'));

	/* Create Follow-Up spins a failed inspection out into a work order — the
	   remediation path now that corrections live on work orders (per Kevin:
	   "new work must be done to complete" a failed inspection). It sits in the
	   kebab whenever items failed; at observations_complete, the review
	   moment, it moves up into the action bar. */
	const canCreateFollowUp = canEdit && hasFailedItems;
	const createFollowUp = () => navigate(`/work-orders/create?follow_up_from_inspection=${inspection.id}`);
	const followUpInBar = canCreateFollowUp && inspection.status === 'observations_complete';

	/* At observations_complete two actions can compete for the primary slot:
	   Create Follow-Up and the forward step, Complete Inspection. Which leads
	   follows the ratings and what has already been spun out — a Critical /
	   Needs Attention item with no follow-up yet means work is owed, so
	   Create Follow-Up leads; once a follow-up exists, or on a clean result,
	   completing leads and Create Follow-Up sits beside it. (Ratings arrive
	   a beat after the record on a cold load, so a failed inspection can show
	   Complete Inspection first; erring that way is right, since a clean
	   result is the common case and never swaps.) */
	const followUpLeads = followUpInBar && followUps.length === 0;

	/* The forward status step is only promoted to the primary slot when nothing
	   more specific claims it. */
	const primaryTransition = !canResumeWorkflow && !followUpLeads ? forwardTransitions[0] : undefined;

	const primaryAction: DetailAction | null = canResumeWorkflow
		? {
			label: 'Resume Workflow',
			onClick: () => setShowWorkflow(true),
			/* Clipboard checklist icon */
			icon: <Icon size="sm"><path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" /></Icon>,
		}
		: followUpLeads
			? { label: 'Create Follow-Up', onClick: createFollowUp }
			: primaryTransition
				? { label: primaryTransition.label, onClick: () => handleStatusTransition(primaryTransition.toStatus) }
				: null;

	/* Supporting actions. Forward transitions that didn't take the primary slot
	   keep brand-outline emphasis; Edit sits with them; PDF generation is a
	   neutral utility that should recede. */
	const detailActions: (DetailAction | false)[] = [
		...forwardTransitions
			.filter((t) => t !== primaryTransition)
			.map((t) => ({ key: t.toStatus, label: t.label, onClick: () => handleStatusTransition(t.toStatus), variant: 'secondary' as const })),
		/* Create Follow-Up stays in reach once a follow-up exists — more than
		   one may be needed — just not as the lead. */
		followUpInBar && !followUpLeads
			&& { key: 'create-follow-up', label: 'Create Follow-Up', onClick: createFollowUp, variant: 'secondary' as const },
		canEdit && { key: 'edit', label: 'Edit', onClick: () => setShowEditModal(true), variant: 'secondary' as const },
		/* Generate PDF — single-click client-side render via jspdf. The
		   pre/post-correction dropdown that lived here is gone intentionally:
		   the team moved off backend PDF rendering in favor of client-side
		   jspdf, which currently emits one full document regardless of status.
		   Audience filtering is a follow-up item on the jspdf renderer. */
		{ key: 'pdf', label: isGeneratingPdf ? 'Generating…' : 'Generate PDF', onClick: handleGeneratePdf, variant: 'quiet' as const, isLoading: isGeneratingPdf },
	];

	/* Overflow actions. Create Follow-Up lives here at every status other
	   than observations_complete (where it has moved up into the bar), and
	   stays visible-but-disabled until an item fails so the path is
	   discoverable. Cancel lives here too — destructive, rarely wanted, and
	   far too easy to fat-finger when it's a full-width red button beside
	   the primary CTA. */
	const detailMenuItems = [
		canEdit && !followUpInBar && {
			label: hasFailedItems ? 'Create Follow-Up' : 'Create Follow-Up (no failed items)',
			onClick: createFollowUp,
			disabled: !hasFailedItems,
		},
		cancelTransition && {
			label: cancelTransition.label,
			onClick: () => handleStatusTransition(cancelTransition.toStatus),
			variant: 'danger' as const,
		},
		canEdit && {
			label: 'Delete',
			onClick: () => requestDelete(inspection.id, inspection.buildingName),
			variant: 'danger' as const,
		},
	];

	return (
		<PageContainer block="inspection-detail-page" dataRef="page-inspection-detail-page">
			{/* --- Back navigation link --- */}
			<BackLink to="/inspections" label="Back to Inspections" />

			{/* --- Page header: building name, status badge, dates ---
			    Stay stacked through the tablet range (768–1024px) and only go
			    side-by-side at desktop. Going row at `tablet` crammed the title
			    against the action buttons in the middle-screen zone; keeping
			    them stacked there gives the buttons a full-width row to wrap into. */}
			{/* Blue accent matches the inspection chip color on the calendar —
			    same #1D4ED8 used by CalendarView's category map. Mirrors the
			    WorkOrderDetailPage treatment. */}
			<div className="inspection-detail-page__header flex flex-col desktop:flex-row desktop:items-center desktop:justify-between gap-12px mb-24px pl-12px border-l-4 border-l-[#1D4ED8] rounded-sm">
				<div>
					<h1 className="page-title text-24px font-bold font-heading text-brand-heading mb-8px">
						{inspection.buildingName}
					</h1>
					<div className="flex items-center gap-8px flex-wrap">
						{/* Category badge — same atom + palette used on the calendar
							so the page reads as the same record visually. */}
						<EventTypeBadge type="inspection" size="sm" />
						{/* Status badge */}
						<span className={['inline-flex items-center px-10px py-2px rounded-full text-12px font-sans font-medium border', STATUS_COLORS[inspection.status] || STATUS_COLORS.pending].join(' ')}>
							{formatLabel(inspection.status)}
						</span>
						{/* Inspection date */}
						<span className="text-14px font-sans text-brand-text-muted">
							{formatDay(inspection.inspectionDate)}
						</span>
					</div>
				</div>

				{/* Every action for this inspection — workflow re-entry, status
				    steps, Edit, PDF, and the overflow menu — in one responsive
				    cluster. See the ACTION CLUSTER block above for how the
				    primary/supporting/overflow split is decided. */}
				<DetailActionBar
					primaryAction={primaryAction}
					actions={detailActions}
					menuItems={detailMenuItems}
					menuAriaLabel={`Actions for inspection ${inspection.id}`}
				/>
			</div>

			{/* --- Tab navigation ---
			    overflow-x-auto + non-shrinking tabs so the strip scrolls
			    sideways on small screens instead of squashing or wrapping
			    (inspections can show up to 4 tabs with counts). */}
			<div className="inspection-detail-page__tabs border-b border-brand-border mb-24px overflow-x-auto">
				<nav className="flex gap-0px -mb-px">
					{[
						{ key: 'details' as DetailTab, label: 'Details' },
						/* Checklist tab — only shows when a checklist
						 * instance is assigned. The inspector rates items
						 * here (Good / Needs Attention / Critical) and fills
						 * in the bottom Recommendations + Follow-Up
						 * textareas. */
						...(checklistItems.length > 0 ? [{ key: 'checklist' as DetailTab, label: `Checklist (${checklistItems.length})` }] : []),
						/* Observation + Completed QC tabs only render once
						 * at least one photo of that kind has been captured.
						 * Hides empty panes on fresh inspections so the
						 * tab strip stays honest about what's in the record. */
						...(observationPhotos.length > 0 ? [{ key: 'observations' as DetailTab, label: `Observations (${observationPhotos.length})` }] : []),
						...(correctionPhotos.length > 0 ? [{ key: 'completed_qc' as DetailTab, label: `Completed QC (${correctionPhotos.length})` }] : []),
					].map((tab) => (
						<button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={[
							'flex-shrink-0 whitespace-nowrap px-16px py-12px text-14px font-sans font-medium border-b-2 transition-colors min-h-44px',
							activeTab === tab.key
								? 'border-brand-primary text-brand-primary'
								: 'border-transparent text-brand-text-muted hover:text-brand-primary hover:border-brand-border',
						].join(' ')}>
							{tab.label}
						</button>
					))}
				</nav>
			</div>

			{/* --- Tab content --- */}
			<div className="inspection-detail-page__panel bg-white shadow rounded-lg border border-brand-border p-24px">
				{/* === Details Tab ===
				    Mirrors the create-form section layout (Customer & Building,
				    Customer Contact, Assignment, Notes, Settings) using
				    the InfoRow / InfoSection display molecules so the read-only
				    detail view follows the same atomic-design composition that
				    the editable JobForm template uses. */}
				{activeTab === 'details' && (
					<div className="grid grid-cols-1 tablet:grid-cols-2 gap-24px">
						{/* Customer & Building section */}
						<InfoSection heading="Customer & Building">
							<InfoRow label="Customer" value={inspection.companyName} />
							<InfoRow label="Building" value={inspection.buildingName} />
							<InfoRow label="Project Address" value={inspection.projectAddress ?? null} />
							{inspection.projectAddressLine2 && (
								<InfoRow label="Address Line 2" value={inspection.projectAddressLine2} />
							)}
						</InfoSection>

						{/* Customer Contact section */}
						<InfoSection heading="Customer Contact">
							<InfoRow label="Name" value={inspection.contactName} />
							<InfoRow label="Phone" value={inspection.contactPhone ?? null} />
							<InfoRow label="Email" value={inspection.contactEmail ?? null} />
						</InfoSection>

						{/* Assignment section — Inspector + primary/additional assignees +
						    priority + scheduling. Mirrors the work-order experience. */}
						<InfoSection heading="Assignment">
							<InfoRow label="Inspector" value={inspection.inspectorName || 'Unassigned'} />
							{/* Primary assignee row — looks up the primary's role from the
							    eager-loaded assignees array (if present) so external
							    vendors render with a "Subcontractor" badge alongside the
							    name, matching how the JobForm picker labels them. */}
							<InfoRow label="Primary Assignee (Corrections)">
								{inspection.assigneeName ? (() => {
									const primary = (inspection.assignees ?? []).find((a) => String(a.id) === String(inspection.assigneeId ?? ''));
									const isSub = (primary?.role ?? (primary as any)?.role_names?.[0]) === 'subcontractor';
									return (
										<span className="inline-flex items-center gap-6px">
											{inspection.assigneeName}
											{isSub && (
												<span className="inline-flex items-center px-6px py-1px text-10px font-sans font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full border border-amber-300">Subcontractor</span>
											)}
										</span>
									);
								})() : (
									<span className="text-brand-text-muted">Not yet assigned</span>
								)}
							</InfoRow>
							{/* Additional assignees are surfaced via the eager-loaded
							    assignees relation. Filter out the primary so we don't
							    list them twice. Subcontractor chips use a contrasting
							    amber pill so external vendors stand out from techs. */}
							{Array.isArray(inspection.assignees) && inspection.assignees.length > 0 && (
								<InfoRow label="Additional Assignees">
									<div className="flex flex-wrap gap-6px">
										{inspection.assignees
											.filter((a) => String(a.id) !== String(inspection.assigneeId ?? ''))
											.map((a) => {
												const first = a.firstName ?? a.first_name ?? '';
												const last = a.lastName ?? a.last_name ?? '';
												const name = `${first} ${last}`.trim() || `User #${a.id}`;
												const isSub = (a.role ?? (a as any).role_names?.[0]) === 'subcontractor';
												const pillClass = isSub
													? 'bg-amber-100 text-amber-800 border border-amber-300'
													: 'bg-brand-primary-soft text-brand-primary';
												return (
													<span key={String(a.id)} className={`inline-flex items-center gap-4px px-8px py-2px text-12px font-sans rounded-full ${pillClass}`}>
														{name}
														{isSub && <span className="text-10px font-semibold uppercase tracking-wider">Sub</span>}
													</span>
												);
											})}
									</div>
								</InfoRow>
							)}
							<InfoRow label="Priority" value={inspection.priority ? inspection.priority.charAt(0).toUpperCase() + inspection.priority.slice(1) : null} />
							<InfoRow label="Inspection Date" value={formatDay(inspection.inspectionDate)} />
							{inspection.completedDate && (
								<InfoRow label="Completed Date" value={formatDate(inspection.completedDate)} />
							)}
							{(inspection.startTime || inspection.endTime) && (
								<InfoRow label="Scheduled Window">
									{inspection.startTime ? new Date(inspection.startTime).toLocaleString() : '—'}
									<span className="text-brand-text-muted mx-6px">–</span>
									{inspection.endTime ? new Date(inspection.endTime).toLocaleString() : '—'}
								</InfoRow>
							)}
						</InfoSection>

						{/* Follow-up work orders — where remediation for a failed
						    inspection actually happens (Create Follow-Up spins one
						    out). Shown whenever there is something to say: the list,
						    or a nudge when items failed and nothing has been spun out
						    yet. A clean inspection with no follow-ups says nothing. */}
						{(followUps.length > 0 || hasFailedItems) && (
							<InfoSection heading="Follow-Up Work Orders">
								<InfoRow label="Work Orders">
									{followUps.length === 0 ? (
										<span className="text-brand-text-muted" data-ref="page-inspection-detail-page__no-follow-ups">
											None yet. Items were rated Needs Attention or Critical, so spin the work out with Create Follow-Up.
										</span>
									) : (
										<div className="flex flex-col gap-8px" data-ref="page-inspection-detail-page__follow-ups">
											{openFollowUps.length > 0 && (
												<span className="text-13px text-brand-text-muted">{`${openFollowUps.length} of ${followUps.length} still open`}</span>
											)}
											<ul className="flex flex-col gap-8px">
												{followUps.map((wo) => {
													const assigneeName = wo.assignee
														? `${wo.assignee.firstName ?? wo.assignee.first_name ?? ''} ${wo.assignee.lastName ?? wo.assignee.last_name ?? ''}`.trim()
														: '';
													return (
														<li key={String(wo.id)} className="flex flex-wrap items-center gap-8px">
															<Link to={`/work-orders/${wo.id}`} className="text-brand-primary hover:underline">
																{`Work Order #${wo.id}${wo.projectName ? ` — ${wo.projectName}` : ''}`}
															</Link>
															<span className={['inline-flex items-center px-8px py-1px rounded-full text-11px font-sans font-semibold border', followUpStatusClass(wo.status)].join(' ')}>
																{formatLabel(wo.status)}
															</span>
															{assigneeName && (
																<span className="text-brand-text-muted">{assigneeName}</span>
															)}
														</li>
													);
												})}
											</ul>
										</div>
									)}
								</InfoRow>
							</InfoSection>
						)}

						{/* Inspection notes — collapses what used to be the
						    "People & Notes" section now that people moved into
						    Assignment above. */}
						<InfoSection heading="Inspection Notes">
							<InfoRow label="General Notes" value={inspection.generalNotes} preserveNewlines />
						</InfoSection>

						{/* Settings section — completion gates, frequency and
						    checklist. No estimate sub-block: inspections
						    aren't priced, so there is no cost to show or
						    approve here. */}
						<InfoSection heading="Settings">
							<InfoRow label="Frequency" value={inspection.frequency ?? null} emptyText="One-time" />
							{/* Checklist row — surfaces the snapshotted source
							    template (if any), its frozen version, and the
							    item count. To change a checklist the user
							    opens Edit and picks a different template. */}
							<InfoRow label="Checklist">
								{checklistInstance?.sourceChecklist ? (
									<>
										{checklistInstance.sourceChecklist.title}
										<span className="text-12px text-brand-text-muted ml-8px">
											v{checklistInstance.sourceChecklist.version} • {checklistInstance.items?.length ?? 0} items
										</span>
									</>
								) : checklistInstance ? (
									<>Custom (no template) <span className="text-12px text-brand-text-muted ml-8px">{checklistInstance.items?.length ?? 0} items</span></>
								) : (
									<span className="text-brand-text-muted">None</span>
								)}
							</InfoRow>
						</InfoSection>

						{/* Timestamps row — full width below the columns */}
						<div className="tablet:col-span-2 border-t border-brand-border pt-16px">
							<div className="flex flex-wrap gap-24px text-12px font-sans text-brand-text-muted">
								<span>Created: {formatDateTime(inspection.createdAt)}</span>
								<span>Updated: {formatDateTime(inspection.updatedAt)}</span>
							</div>
						</div>
					</div>
				)}

				{/* === Checklist Tab === */}
				{/* Per-item ratings + bottom Recommendations / Follow-Up
				 * textareas. Edits are gated to inspectors during the
				 * in_progress window — read-only at any other time. */}
				{activeTab === 'checklist' && (
					<InspectionChecklist
						items={checklistItems}
						onRateItem={handleRateItem}
						recommendationsRemarks={inspection.recommendationsRemarks ?? ''}
						onSaveRecommendationsRemarks={(v) => handleSaveInspectionField('recommendationsRemarks', v)}
						followUpNotes={inspection.followUpNotes ?? ''}
						onSaveFollowUpNotes={(v) => handleSaveInspectionField('followUpNotes', v)}
						disabled={!canWorkObservations}
						isOffline={!isOnline}
						onExpandPhoto={(url, meta) => setLightbox({ url, ...meta })}
					/>
				)}

				{/* === Observations Tab === */}
				{/* Shows observation photos taken during the inspection phase */}
				{activeTab === 'observations' && (
					<PhotoGrid photos={observationPhotos} photoTypeLabel="Observation Photos" editable={canAddObservationPhotos} onDelete={handleDeletePhoto} onPhotoCaptured={(photo) => handlePhotoCaptured('observation', photo)} pendingPhotos={photoUploads.items.filter((item) => item.meta.photoType === 'observation')} onRetryUpload={photoUploads.retry} onDismissUpload={photoUploads.dismiss} />
				)}

				{/* === Completed QC Tab === */}
				{/* Shows correction photos taken after deficiencies were fixed */}
				{activeTab === 'completed_qc' && (
					<PhotoGrid photos={correctionPhotos} photoTypeLabel="Correction Photos" editable={canAddCorrectionPhotos} onDelete={handleDeletePhoto} onPhotoCaptured={(photo) => handlePhotoCaptured('correction', photo)} pendingPhotos={photoUploads.items.filter((item) => item.meta.photoType === 'correction')} onRetryUpload={photoUploads.retry} onDismissUpload={photoUploads.dismiss} />
				)}
			</div>

			{/* === Photo lightbox === */}
			{/* Opened from the Checklist tab when the user expands a
			 *  per-item photo thumbnail. Lives at the page level so it
			 *  layers above the tab content. */}
			<PhotoLightbox isOpen={!!lightbox} imageUrl={lightbox?.url ?? null} caption={lightbox?.caption ?? null} area={lightbox?.area ?? null} onClose={() => setLightbox(null)} />

			{/* === Edit Inspection Modal === */}
			<Modal
				isOpen={showEditModal}
				onClose={requestCloseEditModal}
				title="Edit Inspection"
				size="xl"
			>
				<JobForm
					formType="inspection"
					editId={inspection.id}
					initialData={inspection}
					onDirtyChange={setEditFormDirty}
					onSuccess={() => {
						closeEditModal();
						fetchInspection();
						fetchChecklistInstance();
					}}
				/>
			</Modal>
			{discardEditPrompt}

			{/* === Inspection Completion Workflow Overlay === */}
			{/* Full-screen 3-step flow: Checklist (ratings) → Verify
			    (review + observation photos) → Sign Off (recommendations,
			    follow-up — no signature). On submit the inspection
			    transitions to observations_complete. */}
			{user && (
				<InspectionWorkflow
					isOpen={showWorkflow}
					onClose={() => setShowWorkflow(false)}
					inspectionId={String(inspection.id)}
					inspection={{
						id: inspection.id,
						status: inspection.status,
						recommendationsRemarks: inspection.recommendationsRemarks ?? null,
						followUpNotes: inspection.followUpNotes ?? null,
						companyId: inspection.companyId,
					}}
					inspectionTitle={inspection.buildingName}
					onComplete={() => {
						setShowWorkflow(false);
						fetchInspection();
						fetchChecklistItems();
					}}
				/>
			)}

			{/* The "Notify users" prompt. Null until a status change would
			    actually email someone and the user is admin-tier. */}
			{notifyPrompt}
			{deleteModal}
		</PageContainer>
	);
};

export default InspectionDetailPage;
